import { motion } from 'framer-motion';
import { CloudIcon, CloudRainIcon, CloudSnowIcon, CloudLightningIcon, CloudFogIcon, SunIcon, WindIcon, ThermometerIcon } from 'lucide-react';
import { useWeather } from '../../hooks/useWeather';
import { useTranslation } from 'react-i18next';

export function OutdoorEnvironmentWidget({ assetId = null }: { assetId?: string | null }) {
  const { t } = useTranslation();
  const { site_temperature, weatherCode, pm25, loading } = useWeather(assetId);
  
  const getWeatherInfo = (code: number | null) => {
    if (code === null) return { Icon: CloudIcon, color: 'text-slate-400', label: '---' };
    if (code === 0 || code === 1) return { Icon: SunIcon, color: 'text-amber-400', label: 'Clear' };
    if (code === 2 || code === 3) return { Icon: CloudIcon, color: 'text-slate-200', label: 'Cloudy' };
    if (code === 45 || code === 48) return { Icon: CloudFogIcon, color: 'text-slate-400', label: 'Fog' };
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return { Icon: CloudRainIcon, color: 'text-cyan-400', label: 'Rainy' };
    if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) return { Icon: CloudSnowIcon, color: 'text-sky-200', label: 'Snow' };
    if (code >= 95 && code <= 99) return { Icon: CloudLightningIcon, color: 'text-yellow-400', label: 'Storm' };
    
    return { Icon: CloudIcon, color: 'text-slate-400', label: 'Cloudy' };
  };

  const { Icon: WeatherIcon, color: weatherColor } = getWeatherInfo(weatherCode);

  const pm25Status = pm25 === null ? '-' : pm25 <= 15 ? t('system.pm25_good') : pm25 <= 35 ? t('system.pm25_fair') : pm25 <= 75 ? t('system.pm25_bad') : t('system.pm25_very_bad');
  const pm25Color = pm25 === null ? 'text-slate-500' : pm25 <= 15 ? 'text-emerald-400' : pm25 <= 35 ? 'text-cyan-400' : pm25 <= 75 ? 'text-amber-400' : 'text-red-400';

  if (loading && site_temperature === null) {
    return (
      <div className="w-full min-h-[95px] rounded-2xl bg-[var(--mobile-bg-card)] border border-[var(--mobile-border)] flex items-center justify-center">
        <span className="text-[0.8rem] text-cyan-400 animate-pulse">날씨정보 가져오는 중...</span>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="w-full min-h-[95px] rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm overflow-hidden"
    >
      <div className="flex items-stretch h-[95px]">
        {/* Left: Weather & Temp */}
        <div className="flex-1 p-3 flex flex-col justify-center border-r border-[var(--mobile-border)]">
          <div className="flex items-center justify-between h-full px-1">
            <div className="flex flex-col h-full justify-start pt-1">
              <div className="flex items-center space-x-1.5 mb-2">
                <ThermometerIcon className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[0.7rem] uppercase font-bold tracking-wider text-[var(--mobile-text-muted)]">
                  {t('system.out_temp')}
                </span>
              </div>
              <span className="text-[1.4rem] font-mono font-bold text-[var(--mobile-text-primary)] leading-none mt-1">
                {site_temperature !== null ? `${site_temperature.toFixed(1)}°C` : '--°C'}
              </span>
            </div>
            <div className="flex h-full items-center justify-center">
              <WeatherIcon className={`w-7 h-7 ${weatherColor} drop-shadow-sm`} />
            </div>
          </div>
        </div>

        {/* Right: Air Quality */}
        <div className="flex-1 p-3 flex flex-col justify-center bg-[var(--mobile-bg-sub)]/30">
          <div className="flex items-center justify-between h-full px-1">
            <div className="flex flex-col h-full justify-start pt-1">
              <div className="flex items-center mb-2">
                <div className="flex items-center gap-1">
                  <WindIcon className={`w-3.5 h-3.5 ${pm25Color}`} />
                  <span className="text-[0.7rem] uppercase font-bold tracking-wider text-[var(--mobile-text-muted)]">
                    {t('system.pm25')}
                  </span>
                </div>
              </div>
              <span className="text-[1.4rem] font-mono font-bold text-[var(--mobile-text-primary)] leading-none mt-1">
                {pm25 !== null ? Math.round(pm25) : '--'}
                <span className="text-[0.65rem] ml-1 font-normal text-[var(--mobile-text-muted)] font-sans">µg/m³</span>
              </span>
            </div>
            <div className="flex h-full items-center justify-center">
              <span className={`text-[1.0rem] font-bold ${pm25Color} leading-none`}>
                {pm25Status}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
